import React, { useState } from 'react';
import { Box, FormControl, MenuItem, Select, Button, Typography, Paper, useMediaQuery, useTheme, Chip } from '@mui/material';
import { Account, Vehicle } from '../../state/types';
import { State } from '../../state/reducers';
import { downloadFilesAsync } from '../../services/FileDownloader';
import { reprocessVehicle } from '../../state/vehicle/action_creator';
import { userHasAccess } from '../../services/RoleBasedAccess';
import { useSelector } from 'react-redux';
import { useVehiclesContext } from '../../context/VehiclesContext';

const VehicleOperations: React.FC = () => {
  const { selectedVehicles, setSelectedVehicles, refetch } = useVehiclesContext();

  if (selectedVehicles.length === 0) return null;
  const [selectedOperation, setSelectedOperation] = useState("");
  const user: Account = useSelector((state: State) => state.accountState.user);


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const onVehiclesUpdate = () => {
    setSelectedVehicles([])
    refetch()
  }
  

  const executeOperation = () => {
    console.log(selectedOperation)
    if (!selectedOperation) return;

    switch (selectedOperation) {
      case "downloadImages":
        selectedVehicles.forEach(vehicle => {
          downloadFilesAsync(vehicle)
        });
        break;
      case "cropping":
        let croppedText = ''
        selectedVehicles.forEach(vehicle => {
          reprocessVehicle(vehicle.pk, vehicle.sk, selectedOperation)
          croppedText += `Reprocessing cropped images for ${vehicle.assetIdentifier} \n`
        });
        alert(croppedText)
        break;
      case "featured":
        let featuredText = ''
        selectedVehicles.forEach(vehicle => {
          reprocessVehicle(vehicle.pk, vehicle.sk, selectedOperation)
          featuredText += `Reprocessing featured images for ${vehicle.assetIdentifier} \n`
        });
        alert(featuredText)
        break;
      case "exterior_bgr":
        let backgroundText = ''
        selectedVehicles.forEach(vehicle => {
          reprocessVehicle(vehicle.pk, vehicle.sk, selectedOperation)
          backgroundText += `Reprocessing background replaced images for ${vehicle.assetIdentifier} \n`
        });
        alert(backgroundText)
        break;
      case "syndication":
        let syndicationText = ''
        selectedVehicles.forEach(vehicle => {
          reprocessVehicle(vehicle.pk, vehicle.sk, selectedOperation)
          syndicationText += `Reprocessing syndication for ${vehicle.assetIdentifier} \n`
        });
        alert(syndicationText)
        break;
      case "archive":
        const archiveUpdates: Partial<Vehicle>[] = [];
        selectedVehicles.forEach(vehicle => {
          reprocessVehicle(vehicle.pk, vehicle.sk, selectedOperation);
          archiveUpdates.push({ pk: vehicle.pk, sk: vehicle.sk, archived: true });
        });
        onVehiclesUpdate();
        alert("Archived selected vehicles.");
        break;
      case "unarchive":
        const unarchiveUpdates: Partial<Vehicle>[] = [];
        selectedVehicles.forEach(vehicle => {
          reprocessVehicle(vehicle.pk, vehicle.sk, selectedOperation);
          unarchiveUpdates.push({ pk: vehicle.pk, sk: vehicle.sk, archived: false });
        });
        onVehiclesUpdate();
        alert("Unarchived selected vehicles.");
        break;
      default:
        console.warn("No operation selected");
    }
  };

  return (
    <Paper sx={{ padding: 2, borderRadius: 2, marginBottom: 2 }}>
      {/* 👇 1. Create a flex container for the header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2 // Keep the margin below the header
      }}>
        <Typography variant="h6">
          Vehicle Operations
        </Typography>
        <Button 
          variant="outlined" // Change from "text" to "outlined"
          size="small"
          onClick={() => setSelectedVehicles([])}
          sx={{ 
            textTransform: 'none',
            // Use theme colors for a consistent look
            color: theme.palette.text.secondary, // A subtle grey for the text
            borderColor: theme.palette.divider, // A light grey for the border
            '&:hover': {
              backgroundColor: theme.palette.action.hover, // Add a slight background on hover
              borderColor: theme.palette.text.secondary,
            }
          }}
        >
          Clear ({selectedVehicles.length})
        </Button>
      </Box>
      
      {/* 3. The separate line for the count has been removed */}

      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1.5 : 0 
      }}>
        <FormControl sx={{ 
          width: isMobile ? '100%' : '50%',
          mr: isMobile ? 0 : 2
        }}>
          <Select
            value={selectedOperation}
            onChange={(e) => setSelectedOperation(e.target.value)}
            displayEmpty
            size="small" // A slightly smaller control looks better
          >
            {/* MenuItems remain the same */}
            <MenuItem value="" disabled>Select an Operation...</MenuItem>
            <MenuItem value="downloadImages">Download Images</MenuItem>
            {userHasAccess(user.role, ['Internal']) && [
              <MenuItem value="cropping" key="cropping">Reprocess Cropped</MenuItem>,
              <MenuItem value="featured" key="featured">Reprocess Featured</MenuItem>,
              <MenuItem value="exterior_bgr" key="exterior_bgr">Reprocess Background</MenuItem>
            ]}
            {userHasAccess(user.role, ['Internal', 'Client Plus']) && (
              <MenuItem value="syndication">Send to Syndication</MenuItem>
            )}
            {userHasAccess(user.role, ['Internal']) && (
              <MenuItem value="archive" key="archive">Archive</MenuItem>
            )}
            {selectedVehicles.some(vehicle => !!vehicle.archived) && userHasAccess(user.role, ['Internal']) && (
              <MenuItem value="unarchive">Unarchive</MenuItem>
            )}
          </Select>
        </FormControl>
        
        <Button 
          variant="contained" 
          color="warning" 
          onClick={executeOperation} 
          disabled={!selectedOperation}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          Execute
        </Button>
      </Box>
    </Paper>
  );
};

export default VehicleOperations;